import type { CallbackInterface } from 'recoil'

import { logAppError } from 'Helpers/logger'
import { errorUpdater, loaderUpdater, pageState } from 'State/page'
import type { PageStateType } from 'Types/state'

import type {
  GroupItem,
  PaginationState,
  SearchState,
} from 'Pages/GroupsList/model'
import {
  groupsStatsState,
  modalCopyState,
  modalDeleteState,
  paginationState,
  panelEditState,
  resultsState,
  searchState,
  selectedAllState,
  selectionState,
  userVisibleListsState,
} from 'Pages/GroupsList/recoil/state/state'
import {
  fetchResults,
  getGroupsListStats,
} from 'Pages/GroupsList/services/services'

export const initPageCallback =
  ({ set, reset, ...cbi }: CallbackInterface) =>
  async () => {
    set<PageStateType>(pageState, loaderUpdater(true))

    try {
      const { items, pagination: paginationOut } = await fetchResults(
        {
          searchBy: '',
          groupType: 'All',
        },
        { pageNumber: 1, pageSize: 10, orderBy: 'Id', orderDescending: true },
      )

      set(resultsState, items)
      set(paginationState, (prev) => ({
        ...prev,
        ...paginationOut,
        loading: false,
      }))

      void fetchGroupsListStatsCallback({ reset, set, ...cbi })(
        items.map((item) => item.Id),
      )

      set<PageStateType>(pageState, (prev) => ({ ...prev, status: 'ready' }))
    } catch (error) {
      console.error('### error', error)

      logAppError({
        Component: 'GroupsList',
        Method: 'initPageCallback',
        appError: error,
      })

      set<PageStateType>(pageState, errorUpdater)
    }

    set<PageStateType>(pageState, loaderUpdater(false))
  }

export const resetPageCallback =
  ({ reset }: CallbackInterface) =>
  async () => {
    reset(paginationState)
    reset(resultsState)
    reset(searchState)
    reset(selectionState)
    reset(panelEditState)
    reset(modalDeleteState)
    reset(groupsStatsState)
    reset(modalCopyState)
    reset(userVisibleListsState)
  }

export const fetchResultsCallback =
  ({ reset, set, snapshot, ...cbi }: CallbackInterface) =>
  async (
    p: Partial<PaginationState>,
    s: Partial<SearchState>,
    resetSelection = false,
  ) => {
    set(paginationState, (prev) => ({ ...prev, loading: true }))

    const [prevPagination, prevSearch] = await Promise.all([
      snapshot.getPromise(paginationState),
      snapshot.getPromise(searchState),
    ])

    const newPagination: PaginationState = {
      ...prevPagination,
      pageNumber: p.pageNumber ?? prevPagination.pageNumber,
      pageSize: p.pageSize ?? prevPagination.pageSize,
      orderBy: p.orderBy ?? prevPagination.orderBy,
      orderDescending:
        p.orderDescending !== undefined
          ? p.orderDescending
          : p.orderBy !== undefined && p.orderBy === prevPagination.orderBy
            ? !prevPagination.orderDescending
            : prevPagination.orderDescending,
    }

    const newSearch: SearchState = {
      ...prevSearch,
      ...s,
    }

    try {
      const { items, pagination: paginationOut } = await fetchResults(
        newSearch,
        newPagination,
      )

      set(resultsState, items)
      set(searchState, newSearch)
      set(paginationState, (prev) => ({
        ...prev,
        ...newPagination,
        ...paginationOut,
        loading: false,
      }))

      if (resetSelection) {
        reset(selectionState)
      }

      void fetchGroupsListStatsCallback({ reset, set, snapshot, ...cbi })(
        items.map((item) => item.Id),
      )
    } catch (error) {
      console.error('### error', error)

      logAppError({
        Component: 'GroupsList',
        Method: 'fetchResultsCallback',
        appError: error,
      })

      set<PageStateType>(pageState, errorUpdater)
    }
  }

export const changeSearchCallback =
  ({ set }: CallbackInterface) =>
  async (p: Partial<SearchState>) => {
    set(searchState, (prev) => ({ ...prev, ...p }))
  }

export const toggleSelectionCallback =
  ({ snapshot, set }: CallbackInterface) =>
  async () => {
    const [results, selectedAll, prevSelection] = await Promise.all([
      snapshot.getPromise(resultsState),
      snapshot.getPromise(selectedAllState),
      snapshot.getPromise(selectionState),
    ])

    const filteredResults = results.filter((r) => r.Deleteable)

    if (selectedAll) {
      //  rimuovo da selezione item presenti in results
      set(selectionState, (prev) =>
        prev.filter(
          (item) => !filteredResults.some((res) => item.Id === res.Id),
        ),
      )
    } else {
      //  filter out items already present in selection to avoid duplicates
      const itemsToAdd = filteredResults.filter(
        (i) => !prevSelection.some((p) => p.Id === i.Id),
      )
      set(selectionState, (prev) => [...prev, ...itemsToAdd])
    }
  }

export const toggleItemSelectionCallback =
  ({ snapshot, set }: CallbackInterface) =>
  async (group: GroupItem) => {
    const [results, selection] = await Promise.all([
      snapshot.getPromise(resultsState),
      snapshot.getPromise(selectionState),
    ])

    const filteredResults = results.filter((r) => r.Deleteable)

    if (selection.some((i) => i.Id === group.Id)) {
      set(selectionState, (prev) => prev.filter((i) => i.Id !== group.Id))
    } else if (filteredResults.some((i) => i.Id === group.Id)) {
      set(selectionState, (prev) => [...prev, group])
    }
  }

export const fetchGroupsListStatsCallback =
  ({ set }: CallbackInterface) =>
  async (ids: number[]) => {
    set(groupsStatsState, { stats: [], loading: true })
    try {
      const stats = await getGroupsListStats(ids)
      set(groupsStatsState, { stats, loading: false })
    } catch (error) {
      console.error('### error', error)
      logAppError({
        Component: 'GroupsList',
        Method: 'fetchGroupsListStatsCallback',
        appError: error,
      })
      set<PageStateType>(pageState, errorUpdater)
    }
  }
